/**
 * Customer Portal API Service
 * Handles customer self-service portal operations
 */

import { apiClient as api } from '../apiClient';

// Types
export interface PortalUser {
  id: string;
  customerId: string;
  email: string;
  fullName: string;
  phone?: string;
  status: string;
  timezone: string;
  language: string;
  lastLoginAt?: string;
  notificationPreferences: {
    emailTicketUpdates: boolean;
    emailKbUpdates: boolean;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  user: PortalUser;
}

export interface RegisterRequest {
  invitationToken: string;
  password: string;
  phone?: string;
  timezone?: string;
}

export interface PortalTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  description?: string;
  priority: string;
  status: string;
  categoryName?: string;
  createdAt: string;
  updatedAt: string;
  lastResponseAt?: string;
  comments: PortalTicketComment[];
}

export interface PortalTicketComment {
  id: string;
  body: string;
  authorType: string;
  isPublic: boolean;
  createdAt: string;
}

export interface CreateTicketRequest {
  subject: string;
  description: string;
  categoryId?: string;
  priority?: string;
}

export interface AddCommentRequest {
  body: string;
}

export interface KBArticle {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  body: string;
  categoryName?: string;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  publishedAt: string;
}

export interface KBCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  articleCount: number;
}

export interface UpdateProfileRequest {
  fullName?: string;
  phone?: string;
  timezone?: string;
  language?: string;
  notificationPreferences?: {
    emailTicketUpdates?: boolean;
    emailKbUpdates?: boolean;
  };
}

// API Service
class CustomerPortalService {
  private baseUrl = '/api/customer-portal';
  private portalBaseUrl = '/api/customer-support/portal';

  // =====================================================
  // AUTHENTICATION
  // =====================================================

  /**
   * Login to customer portal
   */
  async login(request: LoginRequest): Promise<LoginResponse> {
    const formData = new URLSearchParams();
    formData.append('username', request.email);
    formData.append('password', request.password);

    const response = await api.post(`${this.baseUrl}/login`, formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    // Store token
    if (response.data.access_token) {
      localStorage.setItem('portal_token', response.data.access_token);
    }

    return {
      accessToken: response.data.access_token,
      tokenType: response.data.token_type,
      expiresIn: response.data.expires_in,
      user: this.mapUser(response.data.user),
    };
  }

  /**
   * Register with invitation token
   */
  async register(request: RegisterRequest): Promise<LoginResponse> {
    const response = await api.post(`${this.baseUrl}/register`, {
      invitation_token: request.invitationToken,
      password: request.password,
      phone: request.phone,
      timezone: request.timezone,
    });

    if (response.data.access_token) {
      localStorage.setItem('portal_token', response.data.access_token);
    }

    return {
      accessToken: response.data.access_token,
      tokenType: response.data.token_type,
      expiresIn: 0,
      user: this.mapUser(response.data.user),
    };
  }

  /**
   * Logout from portal
   */
  async logout(): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/logout`);
    } finally {
      localStorage.removeItem('portal_token');
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`${this.baseUrl}/password/reset-request`, { email });
    return response.data;
  }

  /**
   * Confirm password reset
   */
  async confirmPasswordReset(token: string, newPassword: string): Promise<{ success: boolean }> {
    const response = await api.post(`${this.baseUrl}/password/reset-confirm`, {
      token,
      new_password: newPassword,
    });
    return response.data;
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
    const response = await api.post(`${this.baseUrl}/password/change`, {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  // =====================================================
  // PROFILE
  // =====================================================

  /**
   * Get current user profile
   */
  async getProfile(): Promise<PortalUser> {
    const response = await api.get(`${this.baseUrl}/me`);
    return this.mapUser(response.data);
  }

  /**
   * Update profile
   */
  async updateProfile(request: UpdateProfileRequest): Promise<{ success: boolean }> {
    const response = await api.put(`${this.baseUrl}/me`, {
      full_name: request.fullName,
      phone: request.phone,
      timezone: request.timezone,
      language: request.language,
      notification_preferences: request.notificationPreferences,
    });
    return response.data;
  }

  // =====================================================
  // TICKETS
  // =====================================================

  /**
   * Get my tickets
   */
  async getMyTickets(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ tickets: PortalTicket[]; total: number }> {
    const response = await api.get(`${this.portalBaseUrl}/tickets`, { params });

    return {
      tickets: (response.data.tickets || []).map(this.mapTicket),
      total: response.data.total || 0,
    };
  }

  /**
   * Get ticket details
   */
  async getTicket(ticketId: string): Promise<PortalTicket> {
    const response = await api.get(`${this.portalBaseUrl}/tickets/${ticketId}`);
    return this.mapTicket(response.data.ticket);
  }

  /**
   * Create a new ticket
   */
  async createTicket(request: CreateTicketRequest): Promise<{ id: string; ticketNumber: string }> {
    const response = await api.post(`${this.portalBaseUrl}/tickets`, {
      subject: request.subject,
      description: request.description,
      category_id: request.categoryId,
      priority: request.priority || 'medium',
      source: 'portal',
    });

    return {
      id: response.data.id,
      ticketNumber: response.data.ticket_number,
    };
  }

  /**
   * Add comment to ticket
   */
  async addComment(ticketId: string, request: AddCommentRequest): Promise<{ id: string }> {
    const response = await api.post(`${this.portalBaseUrl}/tickets/${ticketId}/comments`, {
      body: request.body,
      is_public: true,
    });
    return { id: response.data.id };
  }

  /**
   * Request to close ticket
   */
  async requestCloseTicket(ticketId: string): Promise<{ success: boolean }> {
    const response = await api.post(`${this.portalBaseUrl}/tickets/${ticketId}/close`);
    return response.data;
  }

  /**
   * Rate ticket resolution
   */
  async rateTicket(
    ticketId: string,
    rating: number,
    comment?: string
  ): Promise<{ success: boolean }> {
    const response = await api.post(`${this.portalBaseUrl}/tickets/${ticketId}/rate`, {
      rating,
      comment,
    });
    return response.data;
  }

  // =====================================================
  // KNOWLEDGE BASE
  // =====================================================

  /**
   * Search knowledge base
   */
  async searchKB(query: string): Promise<{ results: KBArticle[]; total: number }> {
    const response = await api.get(`${this.portalBaseUrl}/kb/search`, {
      params: { q: query },
    });

    return {
      results: (response.data.results || []).map(this.mapArticle),
      total: response.data.total || 0,
    };
  }

  /**
   * Get article by slug
   */
  async getArticle(slug: string): Promise<KBArticle> {
    const response = await api.get(`${this.portalBaseUrl}/kb/articles/${slug}`);
    return this.mapArticle(response.data.article);
  }

  /**
   * Get KB categories
   */
  async getCategories(): Promise<KBCategory[]> {
    const response = await api.get(`${this.portalBaseUrl}/kb/categories`);
    return (response.data.categories || []).map(this.mapCategory);
  }

  /**
   * Get articles in category
   */
  async getCategoryArticles(slug: string): Promise<{
    category: KBCategory;
    articles: KBArticle[];
  }> {
    const response = await api.get(`${this.portalBaseUrl}/kb/categories/${slug}`);
    return {
      category: this.mapCategory(response.data.category),
      articles: (response.data.articles || []).map(this.mapArticle),
    };
  }

  /**
   * Submit article feedback
   */
  async submitArticleFeedback(
    articleId: string,
    isHelpful: boolean,
    comment?: string
  ): Promise<{ success: boolean }> {
    const response = await api.post(`${this.portalBaseUrl}/kb/articles/${articleId}/feedback`, {
      is_helpful: isHelpful,
      comment,
    });
    return response.data;
  }

  /**
   * Get popular articles
   */
  async getPopularArticles(limit: number = 10): Promise<KBArticle[]> {
    const response = await api.get(`${this.portalBaseUrl}/kb/popular`, {
      params: { limit },
    });
    return (response.data.articles || []).map(this.mapArticle);
  }

  /**
   * Get recent articles
   */
  async getRecentArticles(limit: number = 10): Promise<KBArticle[]> {
    const response = await api.get(`${this.portalBaseUrl}/kb/recent`, {
      params: { limit },
    });
    return (response.data.articles || []).map(this.mapArticle);
  }

  // =====================================================
  // TRAINING (Optional - for integrated LMS)
  // =====================================================

  /**
   * Get available courses
   */
  async getCourses(category?: string): Promise<any[]> {
    const response = await api.get(`${this.portalBaseUrl}/training/courses`, {
      params: { category },
    });
    return response.data.courses || [];
  }

  /**
   * Get my enrolled courses
   */
  async getMyCourses(): Promise<any[]> {
    const response = await api.get(`${this.portalBaseUrl}/training/my-courses`);
    return response.data.courses || [];
  }

  /**
   * Enroll in course
   */
  async enrollInCourse(courseId: string): Promise<{ enrollmentId: string }> {
    const response = await api.post(`${this.portalBaseUrl}/training/courses/${courseId}/enroll`);
    return { enrollmentId: response.data.enrollment_id };
  }

  /**
   * Get my certificates
   */
  async getMyCertificates(): Promise<any[]> {
    const response = await api.get(`${this.portalBaseUrl}/training/certificates`);
    return response.data.certificates || [];
  }

  // =====================================================
  // HELPER METHODS
  // =====================================================

  private mapUser(data: any): PortalUser {
    return {
      id: data.id,
      customerId: data.customer_id,
      email: data.email,
      fullName: data.full_name,
      phone: data.phone,
      status: data.status,
      timezone: data.timezone || 'UTC',
      language: data.language || 'en',
      lastLoginAt: data.last_login_at,
      notificationPreferences: data.notification_preferences || {
        emailTicketUpdates: true,
        emailKbUpdates: false,
      },
    };
  }

  private mapTicket(data: any): PortalTicket {
    return {
      id: data.id,
      ticketNumber: data.ticket_number,
      subject: data.subject,
      description: data.description,
      priority: data.priority,
      status: data.status,
      categoryName: data.category_name,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      lastResponseAt: data.last_response_at,
      comments: (data.comments || []).map((c: any) => ({
        id: c.id,
        body: c.body,
        authorType: c.author_type,
        isPublic: c.is_public,
        createdAt: c.created_at,
      })),
    };
  }

  private mapArticle(data: any): KBArticle {
    return {
      id: data.id,
      title: data.title,
      slug: data.slug,
      excerpt: data.excerpt,
      body: data.body,
      categoryName: data.category_name,
      viewCount: data.view_count || 0,
      helpfulCount: data.helpful_count || 0,
      notHelpfulCount: data.not_helpful_count || 0,
      publishedAt: data.published_at,
    };
  }

  private mapCategory(data: any): KBCategory {
    return {
      id: data.id,
      name: data.name,
      slug: data.slug,
      description: data.description,
      articleCount: data.article_count || 0,
    };
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn(): boolean {
    return !!localStorage.getItem('portal_token');
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem('portal_token');
  }
}

export const customerPortalService = new CustomerPortalService();
export default customerPortalService;
